import { useApiQuery } from '@locus-taxy/encore-ui-network';
import {
  RELATED_GRID_RESOLVE_ENDPOINT_KEY,
  type RelatedGridFilter,
  type RelatedGridResolveResponse,
} from '@locus-taxy/entity-detail/grid-adapter';
import type { DetailPathHop } from '@locus-taxy/entity-detail';

export interface CustomSectionResolution {
  /** The derived parent-scope filter, or `undefined` when the section has no path. */
  filter: RelatedGridFilter | undefined;
  /** True while the resolve request is in flight. */
  isLoading: boolean;
}

/**
 * The resolver reports the match field as authored on the entity (e.g.
 * `nodeId`), but every consumer of this filter — `useScheduleData`'s records
 * fetch and `EntityGrid.Provider`'s `baseCondition` alike — queries entity
 * fields under the `entityData.` envelope; an unprefixed field silently
 * matches nothing (confirmed live: `{field:"nodeId",...}` against
 * `nodeSchedule/search` returns 0 rows for a record that has that exact
 * value, `{field:"entityData.nodeId",...}` returns it). Same fix
 * `grid-adapter`'s `toGridField` already applies for `relatedGrid` sections'
 * own `defaultFilters` — mirrored here rather than re-derived, since it is
 * the same resolver response feeding a different consumer.
 */
function toEntityDataField(field: string): string {
  return field.startsWith('entityData.') ? field : `entityData.${field}`;
}

/** Every precondition the resolve request needs before it can fire. */
function canResolve(
  clientId: string,
  entityName: string,
  instanceId: string,
  hasPath: boolean,
  targetEntity: string | undefined
): boolean {
  return (
    Boolean(clientId && entityName && instanceId) &&
    hasPath &&
    Boolean(targetEntity)
  );
}

/**
 * Resolve a `custom` detail-tab section's own join `path` via the same
 * server-side resolver `relatedGrid` sections use (`entityDetail.relatedGrid.
 * resolve`, DP-5128 Decision 1) — reuses `useRelatedGridResolution`'s endpoint
 * and request/response shape (`grid-adapter`), but not the hook itself: that
 * hook's `enabled` gate requires `section.targetEntity`, a field only
 * `RelatedGridSection` carries. `CustomSection` has no `targetEntity` of its
 * own, so it is derived here from the path's own last hop (the hop that lands
 * on the target entity) — the resolve endpoint 422s without it (confirmed
 * against the real backend; it does not derive `targetEntity` from `path`).
 */
export function useCustomSectionResolution(
  clientId: string,
  entityName: string,
  instanceId: string,
  path: DetailPathHop[] | undefined
): CustomSectionResolution {
  const hasPath = Boolean(path && path.length > 0);
  const targetEntity = path?.[path.length - 1]?.entity;
  const { data, isLoading } = useApiQuery<RelatedGridResolveResponse>({
    key: [
      'entityDetail.customSection.resolve',
      clientId,
      entityName,
      instanceId,
      JSON.stringify(path ?? []),
    ],
    endpointKey: RELATED_GRID_RESOLVE_ENDPOINT_KEY,
    params: { clientId, entityName, instanceId },
    config: {
      method: 'POST',
      body: { request: { targetEntity, path: path ?? [] } },
      enabled: canResolve(
        clientId,
        entityName,
        instanceId,
        hasPath,
        targetEntity
      ),
    },
  });

  const rawFilter = data?.data?.filter;
  return {
    filter: rawFilter
      ? { ...rawFilter, field: toEntityDataField(rawFilter.field) }
      : undefined,
    isLoading: hasPath && isLoading,
  };
}
